import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { useAuth, completeSigninRedirect } from '../hooks/useAuth';

export function CallbackPage() {
  const navigate = useNavigate();
  const { isAuthenticated, authError, login, refreshSession, setAuthError } = useAuth();

  useEffect(() => {
    // Exchanges the authorization code Keycloak redirected back with
    // for tokens via lambdas/auth_token_proxy (see useAuth.tsx's own
    // docstring for why this goes through that proxy rather than
    // Keycloak's /token endpoint directly), then tells AuthProvider to
    // re-read the newly-written localStorage session -- unlike the
    // earlier oidc-client-ts-based version, completeSigninRedirect is
    // a plain async helper with no access to this provider's React
    // state, so that hand-off is explicit here rather than implicit
    // via a library event.
    completeSigninRedirect()
      .then(() => refreshSession())
      .catch((error: unknown) => {
        setAuthError(error instanceof Error ? error.message : 'Sign-in could not be completed.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  return (
    <Box textAlign="center" padding={{ vertical: 'xxxl', horizontal: 'l' }}>
      {authError ? (
        <SpaceBetween size="m" direction="vertical">
          <Alert type="error" statusIconAriaLabel="Error" header="Sign-in failed">
            {authError}
          </Alert>
          <Button variant="primary" onClick={login}>
            Try sign-in again
          </Button>
        </SpaceBetween>
      ) : (
        <SpaceBetween size="s" direction="vertical">
          <Spinner size="large" />
          <Box variant="p" color="text-body-secondary" textAlign="center">
            Completing sign-in...
          </Box>
        </SpaceBetween>
      )}
    </Box>
  );
}
